<template>
  <div class="md:col-span-6 border-t border-gray-200 pt-8">
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-lg font-semibold">
        Sharing Gateways
      </h3>
      <button
        type="button"
        class="btn-primary h-10"
        @click="addNewGateway"
      >
        <PlusCircleIcon class="size-5 mr-2" />
        Add
      </button>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
    </div>

    <div v-else>
      <div v-if="gateways.length > 0" class="mb-6 space-y-4">
        <div v-for="(gateway, index) in gateways" :key="gateway.id || index" class="flex items-center space-x-4">
          <div class="flex-grow">
            <form-input
              v-model="gateway.url"
              placeholder="Gateway URL"
              class="w-full"
              :error-msg="getErrorMessage(gateway)"
            />
          </div>
          <form-switch v-model="gateway.isEnabled" class="mt-3" />
          <button
            type="button"
            class="btn-outline size-9! p-2! group"
            @click="removeGateway(index)"
          >
            <TrashIcon class="size-5 text-red-600 group-hover:text-red-800" />
          </button>
        </div>
      </div>

      <div v-else class="text-gray-500 italic mb-6">
        No sharing gateways defined
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { useToast } from 'vue-toastification';
import FormInput from './FormInput.vue';
import FormSwitch from './FormSwitch.vue';
import { PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { Modal } from '../composables/useModal';
import hashSum from 'hash-sum';

const props = defineProps({
  clusterId: {
    type: [ String, Number ],
    default: null
  }
});

const toast = useToast();
const isLoading = ref(false);
const gateways = ref([]);
const gatewaysHashSum = ref(null);
const errors = ref({});

if (props.clusterId) {
  loadGateways();
}

async function loadGateways() {
  if (!props.clusterId) return;

  isLoading.value = true;
  errors.value = {};

  try {
    const { data, error } = await useFetch(`${apiDomain}/api/cluster/${props.clusterId}/sharing-gateway`).get().json();

    if (error.value) {
      toast.error('Failed to load sharing gateways');
      return;
    }

    gateways.value = data.value || [];
  } catch (err) {
    console.error('Error loading sharing gateways:', err);
    toast.error('Failed to load sharing gateways');
  } finally {
    isLoading.value = false;
    gatewaysHashSum.value = hashSum(gateways.value);
  }
}

function addNewGateway() {
  gateways.value.push({
    clusterId: props.clusterId,
    url: '',
    isEnabled: false,
    isNew: true
  });
}

async function removeGateway(index) {
  const gateway = gateways.value[index];

  if (gateway.id) {
    const confirm = await Modal.confirm({
      title: 'Remove existing gateway',
      body: 'Are you shure?',
      okButton: 'Remove',
      iconColor: 'primary'
    });

    if (!confirm) {
      return;
    }
    // Existing gateway - call API to delete
    deleteGateway(gateway.id, index);
  } else {
    // New gateway - just remove from array
    gateways.value.splice(index, 1);
  }
}

async function deleteGateway(id, index) {
  try {
    const { error } = await useFetch(`${apiDomain}/api/cluster/sharing-gateway/${id}`)
      .delete()
      .json();

    if (error.value) {
      toast.error('Failed to delete sharing gateway');
      return;
    }

    gateways.value.splice(index, 1);
    gatewaysHashSum.value = hashSum(gateways.value);
    toast.success('Sharing gateway deleted');
  } catch (err) {
    console.error('Error deleting sharing gateway:', err);
    toast.error('Failed to delete sharing gateway');
  }
}

function getErrorMessage(gateway) {
  return errors.value[gateway.id || gateway.tempId] || '';
}

function validateGateways() {
  let isValid = true;
  errors.value = {};

  gateways.value.forEach(gateway => {
    const id = gateway.id || gateway.tempId;

    if (!gateway.url || gateway.url.trim() === '') {
      errors.value[id] = 'Gateway URL is required';
      isValid = false;
    }
  });

  return isValid;
}

function isGatewaysChanged() {
  return gatewaysHashSum.value !== hashSum(gateways.value);
}

async function saveGateways() {
  if (!validateGateways()) {
    return false;
  }

  if (!isGatewaysChanged()) {
    return true;
  }

  isLoading.value = true;
  let success = true;

  try {
    // Process each gateway - create new ones, update existing ones
    for (const gateway of gateways.value) {
      if (gateway.isNew) {
        // Create new gateway
        const { error } = await useFetch(`${apiDomain}/api/cluster/sharing-gateway`)
          .post({
            clusterId: props.clusterId,
            url: gateway.url,
            isEnabled: gateway.isEnabled
          })
          .json();

        if (error.value) {
          success = false;
          toast.error(`Failed to create gateway: ${gateway.url}`);
        }
      } else {
        // Update existing gateway
        const { error } = await useFetch(`${apiDomain}/api/cluster/sharing-gateway/${gateway.id}`)
          .put({
            clusterId: props.clusterId,
            url: gateway.url,
            isEnabled: gateway.isEnabled
          })
          .json();

        if (error.value) {
          success = false;
          toast.error(`Failed to update gateway: ${gateway.url}`);
        }
      }
    }

    if (success) {
      // Reload gateways to get updated IDs
      await loadGateways();
    }

    return success;
  } catch (err) {
    console.error('Error saving sharing gateways:', err);
    toast.error('Failed to save sharing gateways');
    return false;
  } finally {
    isLoading.value = false;
  }
}

// Expose methods for parent component
defineExpose({
  saveGateways,
  validateGateways,
  loadGateways,
  isGatewaysChanged
});
</script>
